/**
 * DispatchReadyToolbar.jsx — extracted from Inventory.jsx (iter-121 refactor).
 * Renders the "Missing dims" filter toggle, the missing-dims CSV download,
 * the orphan-SKU backfill CTA, the auto-fill-from-cartons CTA, and the
 * legend. All four CTAs are conditional — the toolbar shrinks gracefully
 * when there's nothing to show.
 *
 * Props:
 *  - showMissingOnly       boolean
 *  - onToggleMissing       () => void
 *  - missingCount          number
 *  - orphanCount           number
 *  - onBackfillOrphans     () => void
 *  - cartonFillCount       number
 *  - onAutoFillDims        () => void
 *  - missingCsvHref        string — pre-built CSV download URL
 */
import React from 'react';
import { WarningCircle, DownloadSimple, Package, Truck } from '@phosphor-icons/react';

export default function DispatchReadyToolbar({
  showMissingOnly,
  onToggleMissing,
  missingCount,
  orphanCount,
  onBackfillOrphans,
  cartonFillCount,
  onAutoFillDims,
  missingCsvHref,
}) {
  return (
    <>
      <button
        type="button"
        onClick={onToggleMissing}
        data-testid="dispatch-ready-filter-toggle"
        className={`flex items-center gap-2 px-3 py-2 border text-xs font-bold uppercase tracking-widest transition ${
          showMissingOnly
            ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
            : 'bg-card text-foreground border-border hover:border-brand hover:text-brand'
        }`}
        title="Show only SKUs missing weight or volume for Dispatch Optimiser"
      >
        <WarningCircle size={14} weight="bold" />
        {showMissingOnly ? `Showing ${missingCount} missing dims` : `Filter · Missing dims (${missingCount})`}
      </button>
      {missingCount > 0 && (
        <a
          href={missingCsvHref}
          data-testid="download-missing-dims-csv"
          className="flex items-center gap-2 px-3 py-2 border border-border bg-card text-foreground hover:border-brand hover:text-brand text-xs font-semibold uppercase tracking-widest transition rounded-md"
          title={`Download the ${missingCount} SKU${missingCount === 1 ? '' : 's'} missing weight/volume — pre-filled CSV to tape-measure and re-upload via CSV Import`}
        >
          <DownloadSimple size={14} weight="bold" />
          Download CSV ({missingCount})
        </a>
      )}
      {orphanCount > 0 && (
        <button
          type="button"
          onClick={onBackfillOrphans}
          data-testid="backfill-orphan-skus-btn"
          className="flex items-center gap-2 px-3 py-2 border border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-bold uppercase tracking-widest transition rounded-md"
          title={`${orphanCount} SKU${orphanCount === 1 ? '' : 's'} have no variant and can't be added to orders — click to auto-create defaults.`}
        >
          <WarningCircle size={14} weight="bold" />
          Fix orphan SKUs ({orphanCount})
        </button>
      )}
      {cartonFillCount > 0 && (
        <button
          type="button"
          onClick={onAutoFillDims}
          data-testid="auto-fill-dims-btn"
          className="flex items-center gap-2 px-3 py-2 border border-sky-500 bg-sky-50 text-sky-800 hover:bg-sky-100 text-xs font-bold uppercase tracking-widest transition rounded-md"
          title={`${cartonFillCount} SKU${cartonFillCount === 1 ? '' : 's'} have carton info but missing per-unit weight/volume — click to divide by case-pack.`}
        >
          <Package size={14} weight="bold" />
          Auto-fill dims from cartons ({cartonFillCount})
        </button>
      )}
      <div
        data-testid="dispatch-ready-legend"
        className="hidden md:flex items-center gap-3 text-[11px] text-muted-foreground font-mono"
      >
        <span className="flex items-center gap-1"><Truck size={12} weight="fill" className="text-emerald-600" /> ready</span>
        <span className="flex items-center gap-1"><Truck size={12} weight="fill" className="text-rose-500" /> missing dims</span>
      </div>
    </>
  );
}
